import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen, ChevronDown, ChevronUp, Link2, Search as SearchIcon } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SupportCTA from '../components/common/SupportCTA';
import HelpBrowseShell, { matchesSearch, anchorId } from '../components/common/HelpBrowseShell';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /help/docs — product documentation grouped by category with a
 * Stripe-style search + category grid at the top. Sections are inline
 * accordions (short, in-context reading). Each section has a stable
 * `#anchor` for shareable deep-links + a copy-link icon.
 */
const UserGuidePage = () => {
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSections, setOpenSections] = useState(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [sRes, cRes] = await Promise.all([
        axios.get(`${API}/pages/user-guide`),
        axios.get(`${API}/pages/docs-categories`),
      ]);
      setSections(sRes.data || []);
      setCategories(cRes.data || []);
      // Auto-open first result on load, first match while searching.
      if ((sRes.data || []).length > 0) {
        setOpenSections(new Set([sRes.data[0].id]));
      }
    } catch (e) {
      console.error('Error loading docs:', e);
    } finally {
      setLoading(false);
    }
  };

  // On-load / on-navigate: honour URL hash → open + scroll to that anchor.
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const target = sections.find(s => anchorId(s.title) === hash);
    if (target) {
      setOpenSections(prev => new Set([...prev, target.id]));
      setTimeout(() => {
        document.getElementById(`section-${target.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [loading, sections]);

  const filteredSections = useMemo(
    () => sections.filter(s => matchesSearch(s, search)),
    [sections, search]
  );

  // Group filtered sections by category, preserving category sort_order.
  const grouped = useMemo(() => {
    const byCat = new Map();
    filteredSections.forEach(s => {
      const key = s.category_id || '__uncat__';
      if (!byCat.has(key)) byCat.set(key, []);
      byCat.get(key).push(s);
    });
    const catList = categories
      .filter(c => byCat.has(c.id))
      .map(c => ({ ...c, sections: byCat.get(c.id) }));
    const uncat = byCat.get('__uncat__') || [];
    return { catList, uncat };
  }, [filteredSections, categories]);

  // Category cards need live counts (post-search-filter so cards reflect
  // what user will actually see when they click). But since we hide
  // categories while searching anyway, this is only used at rest.
  const categoriesWithCount = useMemo(() => {
    const counts = new Map();
    sections.forEach(s => {
      const key = s.category_id || null;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return categories.map(c => ({ ...c, article_count: counts.get(c.id) || 0 }));
  }, [sections, categories]);

  const uncategorisedCount = useMemo(
    () => sections.filter(s => !s.category_id).length,
    [sections]
  );

  const toggleSection = (sectionId) => {
    const next = new Set(openSections);
    if (next.has(sectionId)) next.delete(sectionId); else next.add(sectionId);
    setOpenSections(next);
  };

  const scrollToCategory = (categoryId) => {
    const el = document.getElementById(categoryId ? `cat-${categoryId}` : 'cat-uncategorised');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const copyAnchorLink = async (section) => {
    const url = `${window.location.origin}/help/docs#${anchorId(section.title)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
      // Also update the URL bar so a subsequent share preserves the anchor.
      window.history.replaceState(null, '', `#${anchorId(section.title)}`);
    } catch {
      toast.error('Could not copy link');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  const totalHits = filteredSections.length;
  const hasContent = totalHits > 0;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Link to="/help">
          <Button variant="ghost" className="mb-4" data-testid="docs-back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Help
          </Button>
        </Link>

        <HelpBrowseShell
          title="Docs"
          subtitle="Learn how MARQ works — accounts, plans, practice, live classes, and billing."
          categories={categoriesWithCount}
          uncategorisedCount={uncategorisedCount}
          search={search}
          onSearchChange={setSearch}
          resultsCount={search ? totalHits : null}
          onCategoryClick={scrollToCategory}
        />

        {/* Article list */}
        {hasContent ? (
          <div className="space-y-8" data-testid="docs-article-list">
            {grouped.catList.map(cat => (
              <section key={cat.id} id={`cat-${cat.id}`} className="scroll-mt-20">
                <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#0A7EA4]" />
                  {cat.name}
                  <span className="text-xs font-normal text-slate-400">· {cat.sections.length}</span>
                </h2>
                <div className="space-y-2">
                  {cat.sections.map(s => (
                    <DocSection
                      key={s.id}
                      section={s}
                      open={openSections.has(s.id)}
                      onToggle={() => toggleSection(s.id)}
                      onCopyLink={() => copyAnchorLink(s)}
                    />
                  ))}
                </div>
              </section>
            ))}
            {grouped.uncat.length > 0 && (
              <section id="cat-uncategorised" className="scroll-mt-20">
                <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-slate-400" />
                  Other
                  <span className="text-xs font-normal text-slate-400">· {grouped.uncat.length}</span>
                </h2>
                <div className="space-y-2">
                  {grouped.uncat.map(s => (
                    <DocSection
                      key={s.id}
                      section={s}
                      open={openSections.has(s.id)}
                      onToggle={() => toggleSection(s.id)}
                      onCopyLink={() => copyAnchorLink(s)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          <EmptySearchState search={search} onClear={() => setSearch('')} />
        )}

        <SupportCTA variant="footer" />
      </div>
    </Layout>
  );
};

/**
 * Single doc section — inline accordion + copy-anchor icon on hover.
 * Stable anchor id derived from title (matches backend slug rules).
 */
const DocSection = ({ section, open, onToggle, onCopyLink }) => {
  const anchor = anchorId(section.title);
  return (
    <div
      id={`section-${section.id}`}
      className="border rounded-lg bg-white"
      data-testid={`doc-section-${section.id}`}
    >
      <Collapsible open={open} onOpenChange={onToggle}>
        <div className="flex items-center gap-1 group">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex-1 flex items-center justify-between p-4 text-left hover:bg-slate-50 rounded-lg transition"
              data-testid={`doc-section-toggle-${section.id}`}
            >
              <span
                id={anchor}
                className="scroll-mt-24 font-medium text-slate-900"
              >
                {section.title}
              </span>
              {open
                ? <ChevronUp className="w-5 h-5 text-slate-500" />
                : <ChevronDown className="w-5 h-5 text-slate-500" />}
            </button>
          </CollapsibleTrigger>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onCopyLink(); }}
            className="p-2 mr-2 text-slate-300 hover:text-[#0A7EA4] opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
            title="Copy link to this section"
            data-testid={`doc-section-copy-${section.id}`}
            aria-label="Copy link to this section"
          >
            <Link2 className="w-4 h-4" />
          </button>
        </div>
        <CollapsibleContent>
          <div
            className="px-4 pb-4 pt-1 border-l-2 border-[#0A7EA4]/20 ml-4 user-guide-content"
            dangerouslySetInnerHTML={{ __html: (section.content || '').replace(/&nbsp;/g, ' ') }}
          />
          {Array.isArray(section.video_embeds) && section.video_embeds.length > 0 && (
            <div
              className="border-l-2 border-[#0A7EA4]/20 ml-4 pl-4 pr-4 pt-2 pb-4 space-y-4"
              data-testid={`user-guide-videos-${section.id}`}
            >
              {section.video_embeds.map((v, i) => (
                <div key={v.id || i} className="space-y-1">
                  {v.title && <p className="text-sm font-medium text-slate-700">{v.title}</p>}
                  <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      src={v.url}
                      title={v.title || `Guide video ${i + 1}`}
                      className="absolute inset-0 w-full h-full border-0"
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="fullscreen; autoplay; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

/**
 * Empty state for zero search hits — nudges toward support so the
 * frustration converts into a ticket / content request instead of a
 * dead end.
 */
const EmptySearchState = ({ search, onClear }) => (
  <div
    className="text-center py-12 border rounded-lg bg-slate-50/50"
    data-testid="docs-empty-state"
  >
    <SearchIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
    {search ? (
      <>
        <p className="text-slate-700 font-medium">
          No results for &ldquo;{search}&rdquo;
        </p>
        <p className="text-sm text-slate-500 mt-1">
          Try a different search, or reach out to us below — we&apos;ll write an article about it.
        </p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onClear} data-testid="docs-empty-clear">
          Clear search
        </Button>
      </>
    ) : (
      <>
        <p className="text-slate-700 font-medium">The docs are being prepared.</p>
        <p className="text-sm text-slate-500 mt-1">Please check back later.</p>
      </>
    )}
  </div>
);

export default UserGuidePage;
